import { createRouter, createWebHistory } from 'vue-router'
import IndexPage from '../pages/index.vue'
import CustomersList from '../pages/customers/List.vue'
import CustomerDetails from '../pages/customers/Details.vue'
import FacilitiesList from '../pages/facilities/List.vue'
import FacilityDetails from '../pages/facilities/Details.vue'
import WorksList from "@/pages/works/List.vue";
import WorkDetails from "@/pages/works/Details.vue";
import ContractsList from "@/pages/contracts/List.vue";
import ContractDetails from "@/pages/contracts/Details.vue";
import EmployeesList from "@/pages/employees/List.vue";
import EmployeeDetails from "@/pages/employees/Details.vue";
import ContactsList from "@/pages/contacts/List.vue";
import SearchPage from "@/pages/search.vue"
import MapPage from "@/pages/facilities/map.vue"
import LoginPage from '@/pages/login.vue'
import ResetPage from '@/pages/reset.vue'
import DepartmentsPage from '@/pages/settings/Departments.vue'
import PositionsPage from '@/pages/settings/Positions.vue'
import DocumentTypesPage from '@/pages/settings/DocumentTypes.vue'
import {useAuthStore} from "@/stores/auth.js";
import {useEmployeeStore} from "@/stores/employee.js";




const routes = [
    {
        path: '/login',
        name: 'login',
        component: LoginPage
    },
    {
        path: '/reset',
        name: 'reset',
        component: ResetPage
    },

    {
        path: '/',
        name: 'Home',
        component: IndexPage,
    },
    {
        path: '/search',
        name: 'Search',
        component: SearchPage,
    },
    {
        path: '/customers',
        name: 'customers',
        component: CustomersList,
        meta: { requiresPermission: 'customer.view_customer' }
    },
    {
        path: '/customers/:id',
        name: 'customer',
        component: CustomerDetails,
        props: true,
        meta: { requiresPermission: 'customer.view_customer' }
    },
    {
        path: '/facilities',
        name: 'facilities',
        component: FacilitiesList,
        meta: { requiresPermission: 'facility.view_facility' }
    },
    {
        path: '/facilities/:id',
        name: 'facility',
        component: FacilityDetails,
        props: true,
        meta: { requiresPermission: 'facility.view_facility' }
    },
    {
        path: '/works',
        name: 'works',
        component: WorksList,
        meta: { requiresPermission: 'work.view_work' }
    },
    {
        path: '/works/:id',
        name: 'work',
        component: WorkDetails,
        props: true,
        meta: { requiresPermission: 'work.view_work' }
    },
    {
        path: '/contracts',
        name: 'contracts',
        component: ContractsList,
        meta: { requiresPermission: 'contract.view_contract' }
    },
    {
        path: '/contracts/:id',
        name: 'contract',
        component: ContractDetails,
        props: true,
        meta: { requiresPermission: 'contract.view_contract' }
    },
    {
        path: '/employees',
        name: 'employees',
        component: EmployeesList,
        meta: { requiresPermission: 'employee.view_employeeprofile' }
    },
    {
        path: '/employees/:id',
        name: 'employee',
        component: EmployeeDetails,
        props: true,
        meta: { requiresPermission: 'employee.view_employeeprofile' }
    },
    {
        path: '/contacts',
        name: 'contacts',
        component: ContactsList,
        meta: { requiresPermission: 'contact.view_contact' }
    },
    {
        path: '/map',
        name: 'FacilitiesMap',
        component: MapPage
    },
    {
        path: '/settings/departments',
        name: 'departments',
        component: DepartmentsPage
    },
    {
        path: '/settings/positions',
        name: 'positions',
        component: PositionsPage
    },
    {
        path: '/settings/document-types',
        name: 'documentTypes',
        component: DocumentTypesPage,
        meta: { requiresPermission: 'documents.manage_types' }
    },


]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
})

router.beforeEach(async (to, from, next) => {
    const auth = useAuthStore()
    const employeeStore = useEmployeeStore()

    // Загружаем токены и профиль пользователя, если еще не загружены
    if (!auth.me) {
        auth.loadTokensFromStorage()
        if (auth.access) {
            try {
                await auth.loadMe()
            } catch (e) {
                // Если loadMe упал с 401/403 — токен невалиден, очищаем
                const status = e?.response?.status
                if (status === 401 || status === 403) {
                    await auth.logout()
                }
            }
        }
    }

    // Проверка аутентификации для защищенных страниц
    const publicPages = new Set(['/login', '/reset'])
    if (!publicPages.has(to.path) && !auth.isAuthenticated) {
        return next({ path: '/login', query: { redirect: to.fullPath } })
    }

    // Загружаем permissions текущего пользователя, если еще не загружены
    if (auth.isAuthenticated && !employeeStore.currentUser) {
        if (import.meta.env.DEV) console.log('[router.beforeEach] Loading current user...')
        try {
            await employeeStore.fetchCurrentUser()
            if (import.meta.env.DEV) console.log('[router.beforeEach] Current user loaded:', employeeStore.currentUser)
        } catch (e) {
            console.error('[router.beforeEach] Ошибка загрузки данных текущего пользователя:', e)
        }
    }

    // Проверка прав доступа для защищенных маршрутов
    const requiredPermission = to.meta?.requiresPermission
    if (requiredPermission) {
        if (import.meta.env.DEV) console.log(`[router.beforeEach] Route requires permission: "${requiredPermission}"`)
        if (import.meta.env.DEV) console.log('[router.beforeEach] Current user:', employeeStore.currentUser)

        // Суперпользователь имеет все права
        if (employeeStore.currentUser?.is_superuser === true) {
            if (import.meta.env.DEV) console.log('[router.beforeEach] User is superuser, granting access')
            return next()
        }

        // Backend возвращает права в поле effective_permissions, а не permissions
        const perms = employeeStore.currentUser?.effective_permissions || employeeStore.currentUser?.permissions
        if (import.meta.env.DEV) console.log('[router.beforeEach] User permissions:', perms)
        let hasPermission = false

        // Если permissions это объект { "perm": true }
        if (perms && typeof perms === 'object' && !Array.isArray(perms)) {
            hasPermission = perms[requiredPermission] === true
            if (import.meta.env.DEV) console.log(`[router.beforeEach] Object check for "${requiredPermission}":`, hasPermission)
        }
        // Если permissions это массив
        else if (Array.isArray(perms)) {
            hasPermission = perms.includes(requiredPermission)
            if (import.meta.env.DEV) console.log(`[router.beforeEach] Array check for "${requiredPermission}":`, hasPermission)
        }

        if (!hasPermission) {
            if (import.meta.env.DEV) console.warn(`[router.beforeEach] Доступ запрещен: отсутствует право "${requiredPermission}"`)
            return next({ path: '/', replace: true })
        }

        if (import.meta.env.DEV) console.log(`[router.beforeEach] Access granted for "${requiredPermission}"`)
    }

    next()
})

export default router